import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { auth, formatErr, postAuthPath } from "@/lib/api";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export function Login() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("driver@haulguard.com");
  const [password, setPassword] = useState("Haul2026!");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const u = await auth.login(email, password); setUser(u); nav(postAuthPath()); toast.success("Welcome back"); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return <AuthShell title="Sign in" subtitle="Welcome back to the shop floor.">
    <form onSubmit={submit} className="space-y-4">
      <Field label="Email" testid="in-email" value={email} onChange={setEmail} type="email" />
      <Field label="Password" testid="in-password" value={password} onChange={setPassword} type="password" />
      <button data-testid="btn-login" disabled={loading} className="btn-amber w-full">
        {loading ? "Signing in…" : <>Sign in <ArrowRight size={18} weight="bold"/></>}
      </button>
    </form>
    <Divider/>
    <button onClick={google} data-testid="btn-google" className="w-full border-2 border-primary py-3 px-4 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 hover:bg-primary hover:text-white transition-colors">
      <GoogleLogo size={18} weight="bold"/> Continue with Google
    </button>
    <p className="mt-6 text-sm text-center">New here? <Link to="/register" className="text-accent font-bold underline">Create an account</Link></p>
  </AuthShell>;
}

export function Register() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const u = await auth.register(form); setUser(u); nav(postAuthPath()); toast.success("Account created"); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return <AuthShell title="Create your account" subtitle="Start your first vehicle record in under a minute.">
    <form onSubmit={submit} className="space-y-4">
      <Field label="Full name" testid="in-name" value={form.name} onChange={set("name")} />
      <Field label="Company (optional)" testid="in-company" value={form.company_name} onChange={set("company_name")} required={false} />
      <Field label="Email" testid="in-email" value={form.email} onChange={set("email")} type="email" />
      <Field label="Password" testid="in-password" value={form.password} onChange={set("password")} type="password" />
      <button data-testid="btn-register" disabled={loading} className="btn-amber w-full">
        {loading ? "Creating…" : <>Create account <ArrowRight size={18} weight="bold"/></>}
      </button>
    </form>
    <Divider/>
    <button onClick={google} data-testid="btn-google" className="w-full border-2 border-primary py-3 px-4 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 hover:bg-primary hover:text-white transition-colors">
      <GoogleLogo size={18} weight="bold"/> Continue with Google
    </button>
    <p className="mt-6 text-sm text-center">Already have an account? <Link to="/login" className="text-accent font-bold underline">Sign in</Link></p>
  </AuthShell>;
}

function Field({ label, testid, value, onChange, type="text", required=true }) {
  return <label className="block">
    <span className="eyebrow block mb-1">{label}</span>
    <input data-testid={testid} type={type} required={required} value={value} onChange={(e)=>onChange(e.target.value)}
      className="w-full border-2 border-border focus:border-accent px-3 py-3 outline-none text-base" />
  </label>;
}
function Divider() { return <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px bg-border flex-1"/>OR<div className="h-px bg-border flex-1"/></div>; }

function AuthShell({ title, subtitle, children }) {
  return <div className="min-h-screen bg-background grid-pattern flex flex-col">
    <header className="bg-primary text-primary-foreground border-b-4 border-accent">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 h-14">
        <Link to="/" className="flex items-center gap-2 font-display font-black tracking-tight text-lg">
          <span className="inline-block w-2 h-6 bg-accent" aria-hidden /> HAULGUARD
        </Link>
      </div>
    </header>
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-border/60 p-7 corner-bracket relative">
        <div className="eyebrow text-accent mb-2">Haulguard</div>
        <h1 className="font-display font-black text-3xl tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>;
}
